import React from "react";
import PropTypes from "prop-types";
import "./MemberCard.css";
import Card from "@mui/joy/Card";
import Typography from "@mui/joy/Typography";
import Button from "@mui/joy/Button";
import Chip from "@mui/joy/Chip";
import { Box, Stack } from "@mui/joy";
import avatar from "../../Male_Avatar.jpg";

function getPartyBorderColor(party) {
  if (party === "Democratic") return "#0075b2";
  if (party === "Republican") return "#c1121f";
  return "#8899a8";
}

const MemberCard = ({ member }) => {
  const goToWebsite = () => {
    window.open(member?.officialWebsiteUrl, "_blank", "noopener,noreferrer");
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      goToWebsite();
    }
  };

  const borderColor = getPartyBorderColor(member.party);
  const location = [
    member.state,
    member.district ? `District ${member.district}` : null,
  ]
    .filter(Boolean)
    .join(" • ");

  return (
    <Card
      className="member-card"
      variant="plain"
      sx={{
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        background: "white",
        border: "none",
        borderTop: `8px solid ${borderColor}`,
        boxShadow: "0px 1px 1.5px rgba(166,175,195,0.4)",
        borderRadius: "4px",
        padding: "32px 24px 24px",
        gap: 2,
        transition: "box-shadow 0.15s ease",
        "&:hover": {
          boxShadow: "0px 4px 16px rgba(166,175,195,0.65)",
        },
        "@media (prefers-reduced-motion: reduce)": {
          transition: "none",
        },
      }}
    >
      <Stack direction="row" spacing={2} sx={{ alignItems: "flex-start" }}>
        <Box
          sx={{
            width: "70px",
            aspectRatio: "70 / 87",
            borderRadius: "4px",
            overflow: "hidden",
            flexShrink: 0,
            alignSelf: "stretch",
          }}
        >
          <img
            src={member.imageUrl || avatar}
            alt={`${member.fullName} portrait`}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        </Box>
        <Stack sx={{ gap: "4px", flex: 1, minWidth: 0 }}>
          <Chip
            size="sm"
            sx={{
              backgroundColor: "#e5e7eb",
              color: "#374151",
              fontWeight: 500,
              fontSize: "12px",
              borderRadius: "24px",
              alignSelf: "flex-start",
              border: "none",
            }}
          >
            {member.party}
          </Chip>
          <Typography
            sx={{
              fontSize: "24px",
              fontWeight: 600,
              lineHeight: "30px",
              color: "#181d27",
            }}
          >
            {member.fullName}
          </Typography>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 400,
              lineHeight: "22px",
              color: "#637381",
              textTransform: "uppercase",
            }}
          >
            {location}
          </Typography>
        </Stack>
      </Stack>

      {member.officialWebsiteUrl && (
        <Button
          variant="outlined"
          onClick={goToWebsite}
          onKeyDown={handleKeyDown}
          aria-label={`Visit ${member.firstName}'s official website`}
          sx={{
            width: "100%",
            borderRadius: "6px",
            borderColor: "#0075b2",
            color: "#0075b2",
            fontWeight: 600,
            fontSize: "16px",
            minHeight: "48px",
            backgroundColor: "white",
            "&:hover": {
              backgroundColor: "rgba(0, 117, 178, 0.04)",
              borderColor: "#0075b2",
            },
          }}
        >
          Visit {member.firstName}&apos;s Website
        </Button>
      )}
    </Card>
  );
};

MemberCard.propTypes = {
  member: PropTypes.object.isRequired,
};

export default MemberCard;
